import type { Static, TSchema } from "@sinclair/typebox";
// `Value` interprets the schema; `TypeCompiler` would generate a checker with
// `new Function`, which Cloudflare Workers refuse — and it happens at route
// registration, so the preview Worker threw before it could serve anything.
// The editor validates a handful of small request bodies, so an interpreter is
// the cheaper trade than two validation paths to keep in step.
import { Value } from "@sinclair/typebox/value";
import { ApiError, Problems } from "@zuplo/errors";
import type { ValidationTargets } from "hono";
import { validator } from "hono/validator";

/** How each request part is named in validation error messages. */
const targetNames: Record<keyof ValidationTargets, string> = {
  json: "body",
  form: "body",
  query: "query",
  param: "params",
  header: "headers",
  cookie: "cookies",
};

/**
 * Validates part of a request against a TypeBox schema, and makes the result
 * available to the handler as `c.req.valid(target)`.
 *
 * Failures are thrown as an `ApiError` so they come back as problem+json from
 * the shared error handler, with the offending fields under `errors` — the same
 * shape the Management API returns for a bad request.
 */
export function validate<
  Target extends keyof ValidationTargets,
  T extends TSchema,
>(target: Target, schema: T) {
  return validator(target, (value) => {
    if (Value.Check(schema, value)) {
      return value as Static<T>;
    }

    throw new ApiError({
      ...Problems.BAD_REQUEST,
      detail: `An error occurred when validating the ${targetNames[target]}.`,
      errors: [...Value.Errors(schema, value)].map((error) => ({
        path: error.path,
        message: error.message,
        value: error.value,
      })),
    });
  });
}
